import { Fetcher, objectToFormData, getFirstFromArray } from '@this/src/util';
import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@this/shared/ui/inputs/button';
import useApi from '@this/components/shared/hooks/use_api';
import { Loading } from '@this/shared/ui/feedbacks/loading';

import type { OrganizationRoleResponse } from '@this/domain/organization_role/organization_role';
import OrganizationRole from '@this/domain/organization_role/organization_role';
import { OrganizationResource } from '@this/domain/organization_role/organization_resource';
import { OrganizationRoleForm } from '@this/components/organization/organization_roles/organization_role_form';
import { OrganizationBody, OrganizationTitle } from '../organization.style';

const Roles = () => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targetRole, setTargetRole] = useState<OrganizationRole | null>(null);
  const { data, isLoading } = useApi<OrganizationRoleResponse>('/organization/roles.json');
  const roles = data ? data.roles.map(args => new OrganizationRole(args)) : [];

  const styles = useMemo(() => getStyles(), []);

  // テンプレート用のロール
  const templateRole = getFirstFromArray(roles, 'organization_roles');

  const skeletonRole = () => {
    if (!templateRole) {
      throw new Error('templateRole is not loaded');
    }
    const resources = templateRole.resources.map(
      r =>
        new OrganizationResource({
          id: 0,
          resource_id: r.resourceId,
          resource_name: r.resourceName,
          resource_path: r.resourcePath,
          read_permission: false,
          write_permission: false
        })
    );

    const organizationRole = new OrganizationRole({ id: 0, name: '', organization_role_resources: [] });
    organizationRole.resources = resources;
    return organizationRole;
  };
  const createRole = () => {
    try {
      setTargetRole(skeletonRole());
      setShowModal(true);
    } catch (error) {
      console.error('役割の作成に失敗しました:', error);
    }
  };
  const editRole = (role: OrganizationRole) => {
    setTargetRole(role);
    setShowModal(true);
  };

  const updateRoleResource = useCallback(async (role: OrganizationRole) => {
    setSaving(true);
    try {
      await Fetcher.put('/organization/roles.json', objectToFormData(role));
      // 権限を変えたタイミングでサイドバーに表示される情報も変更したいため、
      // データを読み込みなおす目的でリロードを行う
      location.reload();
    } catch (e) {
      console.error('役割の更新に失敗しました:', e);
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteRoleResource = useCallback(async (role: OrganizationRole) => {
    setSaving(true);
    try {
      const response = await Fetcher.delete<{
        success: boolean;
        message?: string;
      }>(`/organization/roles/${role.id}.json`);
      if (!response.success) {
        return response.message;
      }
      location.reload();
    } catch (e) {
      return e?.response?.data?.message || '役割の削除に失敗しました';
    } finally {
      setSaving(false);
    }
    return null;
  }, []);

  return (
    <div>
      <OrganizationTitle>役割・権限設定</OrganizationTitle>
      <OrganizationBody>
        <Button
          style={styles.roleButton}
          className="roles__role-button"
          onClick={createRole}
          disabled={!templateRole}
        >
          役割を追加
        </Button>
        {isLoading ? (
          <Loading />
        ) : !templateRole ? (
          <div>役割データの取得に失敗しました。</div>
        ) : (
          <table style={styles.table} className="roles__table">
            <thead>
              <tr>
                <th style={styles.th} className="roles__th" />
                {roles.map((role, i) => (
                  <th key={role.id} colSpan={1} style={styles.th} className="roles__th">
                    {role.name}
                    {/*
                    アドホックな対応だが、デフォルトで作成される管理者、一般ユーザーについては、編集のリンクを表示しない。
                    */}
                    {role.name !== '管理者' && role.name !== '一般ユーザー' && (
                      <Button
                        color="sub"
                        size="small"
                        onClick={() => editRole(role)}
                        style={styles.editButton}
                        className="roles__edit-button"
                      >
                        (編集)
                      </Button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.td} className="roles__td" />
                {roles.map((role, i) => (
                  <React.Fragment key={role.id}>
                    <td style={styles.resourceTd} className="roles__resource-td">
                      利用権限
                    </td>
                    {/*
                      <ResourceTd>編集権限</ResourceTd>
                    */}
                  </React.Fragment>
                ))}
              </tr>
              {templateRole.resources.map((r, i) => {
                const baseResourceId = r.resourceId;
                return (
                  <tr key={r.resourceId}>
                    <td style={styles.td} className="roles__td e2e-resource-name">
                      {r.resourceName}
                    </td>
                    {roles.map((role, j) => {
                      const matchingResource = role.resources.find(res => res.resourceId === baseResourceId);
                      return (
                        <React.Fragment key={role.id}>
                          <td style={styles.resourceTd} className="roles__resource-td">
                            {matchingResource?.readPermission ? '◯' : 'ｘ'}
                          </td>
                          {/*
                            <ResourceTd>{matchingResource?.writePermission ? '◯' : 'ｘ'}</ResourceTd>
                          */}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </OrganizationBody>

      {targetRole && (
        <OrganizationRoleForm
          open={showModal}
          role={targetRole}
          submitting={saving}
          onAbort={() => setShowModal(false)}
          onSubmit={newRole => updateRoleResource(newRole)}
          onDelete={() => deleteRoleResource(targetRole)}
        />
      )}
    </div>
  );
};

const getStyles = () => ({
  table: {
    margin: 0,
    fontSize: '12px',
    width: '100%'
  },
  th: {
    padding: '8px'
  },
  td: {
    padding: '8px'
  },
  resourceTd: {
    padding: '8px',
    textAlign: 'center' as const
  },
  roleButton: {
    display: 'block',
    width: '90px',
    margin: '0 15px',
    padding: '5px 0'
  },
  editButton: {
    border: 'none'
  }
});

export default Roles;
